import React from 'react'
import IconButton from '@mui/material/IconButton'
import { FormControl, SelectChangeEvent } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import {
  ButtonV2,
  LabraToolTipV2,
  SelectV2,
  TextInputV2,
} from '@labrav/react-components'
import type { FormField } from '@labrav/react-components/lib/@types/types/forms'
import clsx from 'clsx'

import type {
  ErrorUsers,
  HandleType,
  UserType,
} from '../../modules/addUserForm/reducer'
import { useStyles } from './AddNewUserModal.styles'
import type { AddUserFormRow } from './AddNewUserModal.helpers'

interface AddUserRowProps {
  users: AddUserFormRow<UserType>[]
  handleInputChange: (event: HandleType, idChanging: string) => void
  errors: ErrorUsers[]
  addNewMemberFields: FormField
  handleRemoveClick: (id: string) => void
  handleAddClick: () => void
  handleBlurInput: (event: HandleType, idChanging: string) => void
  isDisable: boolean
  formDisabled?: boolean
  formDisabledToolTipMessage?: string
}

export const AddUserRow = ({
  users,
  handleInputChange,
  errors,
  addNewMemberFields,
  handleRemoveClick,
  handleAddClick,
  handleBlurInput,
  isDisable,
  formDisabled,
  formDisabledToolTipMessage,
}: AddUserRowProps) => {
  const { classes } = useStyles()

  const handleChange = (id: string) => (e: SelectChangeEvent) => {
    handleInputChange(
      { name: e.target.name as string, value: e.target.value as string },
      id
    )
  }

  const handleBlur =
    (id: string) =>
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      handleBlurInput(
        { name: e.target.name as string, value: e.target.value as string },
        id
      )
    }

  return (
    <div>
      <form autoComplete="off" data-testid="add-new-user-form">
        {users.map(({ id, data: user }, i) => {
          const isErrr = errors.find(
            ({ index, touched }) => index === i && touched
          )?.error
            ? true
            : false
          return (
            <div key={id} data-testid="add-user-row" className="box">
              <div className={classes.formContainer}>
                <div className={classes.fieldWidth}>
                  <FormControl className={classes.formControl}>
                    <LabraToolTipV2
                      tooltipPlacement="bottom-start"
                      description={
                        formDisabled ? formDisabledToolTipMessage : ''
                      }
                      tooltipPlacmentGap="32px"
                    >
                      <TextInputV2
                        keyName="email"
                        title="Email address"
                        value={user.email}
                        placeHolder=""
                        onChange={handleChange(id)}
                        isReadOnly={formDisabled || false}
                        isRequired={false}
                        onBlur={handleBlur(id)}
                        isError={isErrr}
                        errorText={
                          errors.find(
                            ({ index, touched }) => index === i && touched
                          )?.error || ''
                        }
                        type="email"
                        showSpinner={false}
                      />
                    </LabraToolTipV2>
                  </FormControl>
                </div>

                <div className={classes.fieldWidth}>
                  <LabraToolTipV2
                    tooltipPlacement="bottom-start"
                    description={formDisabled ? formDisabledToolTipMessage : ''}
                    tooltipPlacmentGap="32px"
                  >
                    <SelectV2
                      field={{ ...addNewMemberFields }}
                      onChange={handleChange(id)}
                      showNone={false}
                    />
                  </LabraToolTipV2>
                </div>
                <div
                  className={clsx(
                    classes.iconContainer,
                    isErrr ? classes.iconContainerErr : ''
                  )}
                >
                  {users.length !== 1 && (
                    <>
                      <IconButton
                        className={classes.deleteIcon}
                        onClick={() => handleRemoveClick(id)}
                        data-testid="remove-btn"
                        disabled={formDisabled}
                        size="large"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          width={'18px'}
                          height={'18px'}
                        />
                      </IconButton>
                    </>
                  )}
                </div>
              </div>
              {users.length - 1 === i && (
                <LabraToolTipV2
                  tooltipPlacement="bottom-start"
                  description={formDisabled ? formDisabledToolTipMessage : ''}
                  tooltipPlacmentGap="32px"
                >
                  <ButtonV2
                    styleType={'link'}
                    smallVariant
                    onClick={handleAddClick}
                    disabled={formDisabled || isDisable}
                    data-testid="add-one-more-user-btn"
                  >
                    Add a team member
                  </ButtonV2>
                </LabraToolTipV2>
              )}
            </div>
          )
        })}
      </form>
    </div>
  )
}
